import { useEffect, useState } from 'react';
import logo from '@/assets/images/logo.png';
import useTrialStore from '@/stores/TrialStore';
import { CheckCircle, Download, Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function TrialCompletion() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSetupIntentId = useTrialStore((state) => state.setSetupIntentId);
  const trial_id = useTrialStore((state) => state.trial_id);
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    const processPaymentResult = async () => {
      try {
        // Check if we have subscription data from navigation state
        if (location.state?.subscriptionData && location.state?.setupIntentId) {
          setSubscriptionData(location.state.subscriptionData);
          setSetupIntentId(location.state.setupIntentId);
          setStatus('success');
          return;
        }

        // If no subscription data, check if we have a valid trial_id
        if (!trial_id) {
          toast.error(
            'Invalid session. Please start from the onboarding signup page.'
          );
          navigate('/onboarding/signup');
          return;
        }

        // If we reach here, it means the user accessed the page directly without proper flow
        toast.error(
          'Invalid access. Please start from the onboarding signup page.'
        );
        navigate('/onboarding/signup');
      } catch (error) {
        console.error('Error processing payment result:', error);
        setStatus('error');
        toast.error('Error processing payment');
      }
    };

    processPaymentResult();
  }, [location.state, setSetupIntentId, trial_id, navigate]);

  const handleContinue = () => {
    navigate('/onboarding/setup');
  };

  const handleDownloadInvoice = () => {
    if (subscriptionData?.invoice_pdf) {
      window.open(subscriptionData.invoice_pdf, '_blank');
    }
  };

  const handleRestartProcess = () => {
    navigate('/onboarding/signup');
  };

  // Format currency amount
  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Format trial end date
  const formatTrialEndDate = (trialEnd) => {
    return new Date(trialEnd * 1000).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className='flex h-screen bg-background'>
      {/* Left side - 40% */}
      <div className='relative flex w-2/5 flex-col justify-center bg-background px-8 pb-8 pt-4'>
        <div className='mx-auto w-full max-w-md space-y-6'>
          {/* Logo */}
          <div className='flex shrink-0 items-start justify-center'>
            <a
              href='https://getrevio.com'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-2'
            >
              <img className='h-12 w-auto' src={logo} alt='Revio' />
              <span className='text-2xl font-semibold'>Revio</span>
            </a>
          </div>

          {/* Invoice Card - Only show when we have subscription data */}
          {subscriptionData && (
            <Card className='w-full'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-xl'>
                  You have successfully subscribed!
                </CardTitle>
                <div className='space-y-2'>
                  <p className='text-2xl font-bold'>
                    {formatAmount(
                      subscriptionData.monthly_amount,
                      subscriptionData.currency
                    )}{' '}
                    per month
                  </p>
                </div>
              </CardHeader>
              <CardContent className='space-y-4'>
                {/* Invoice Breakdown */}
                <div className='space-y-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm'>
                      Revio monthly Plan (Billed monthly)
                    </span>
                    <span className='text-sm font-medium'>
                      {formatAmount(
                        subscriptionData.monthly_amount,
                        subscriptionData.currency
                      )}
                    </span>
                  </div>
                  <Separator />
                  <div className='flex items-center justify-between'>
                    <span className='text-sm'>Total paid today</span>
                    <span className='text-sm font-medium text-green-600'>
                      {subscriptionData.formatted_amount_paid}
                    </span>
                  </div>
                </div>

                {/* Footer Links */}
                <div className='space-y-2 pt-4'>
                  <div className='flex items-center justify-between text-xs text-muted-foreground'>
                    <span>Powered by stripe</span>
                    <div className='flex space-x-4'>
                      <a
                        href='https://www.getrevio.com/term-and-conditions'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='hover:underline'
                      >
                        Privacy Policy
                      </a>
                      <a
                        href='https://www.getrevio.com/term-and-conditions'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='hover:underline'
                      >
                        Terms
                      </a>
                    </div>
                  </div>

                  {/* Download Button */}
                  <Button
                    variant='outline'
                    className='mt-4 w-full'
                    size='sm'
                    onClick={handleDownloadInvoice}
                    disabled={!subscriptionData.invoice_pdf}
                  >
                    <Download className='mr-2 h-4 w-4' />
                    Download Invoice
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Separator orientation='vertical' />

      {/* Right side - 60% */}
      <div className='w-3/5 bg-background p-4'>
        <div className='mx-auto flex h-full max-w-lg flex-col items-center justify-center space-y-6 text-center'>
          {status === 'processing' ? (
            <>
              <div className='mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary'>
                <Loader2 className='h-8 w-8 animate-spin text-white' />
              </div>
              <h1 className='text-3xl font-bold text-gray-900'>
                Processing your subscription...
              </h1>
              <p className='max-w-md text-base text-muted-foreground'>
                Please wait while we set up your account.
              </p>
            </>
          ) : status === 'error' ? (
            <>
              <div className='mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive'>
                <CheckCircle className='h-8 w-8 text-white' />
              </div>
              <h1 className='text-3xl font-bold text-gray-900'>
                Something went wrong
              </h1>
              <p className='max-w-md text-base text-muted-foreground'>
                We encountered an issue processing your payment. Please try
                again.
              </p>
              <Button onClick={handleRestartProcess} variant='outline'>
                Start Over
              </Button>
            </>
          ) : (
            <>
              {/* Success Icon */}
              <div className='mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary'>
                <CheckCircle className='h-8 w-8 text-white' />
              </div>

              {/* Main Heading */}
              <h1 className='text-3xl font-bold'>Thank you for subscribing!</h1>

              {/* Instructional Text */}
              <p className='max-w-md text-base text-muted-foreground'>
                You are all set to use Revio. Please login by clicking on the
                button below.
              </p>

              {/* Call to Action Button */}
              <Button onClick={handleContinue}>Go to Revio</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
